// Именные приглашения на подбор вузов — серверные функции.
//
// Таблица edu_guest_invites закрыта RLS без единой политики: пишем и читаем
// только здесь, сервис-ролью. Создание и список — за assertAdmin. Страница
// приглашения читает строку ТОЛЬКО по токену; заметка админа и автор наружу
// не отдаются никогда.

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { forbidden } from "@/lib/edu-forbidden.server";
import { GUEST_GRADES, GUEST_LANGS, isValidGuestName, type GuestGrade, type GuestLang } from "@/edu/guest";
import {
  INVITE_TOKEN_RE,
  INVITE_TTL_DAYS,
  guestInvitePath,
  inviteWhatsAppHref,
  type InviteView,
} from "@/edu/guest-invite";
import { newInviteToken, previewOrigin, readInvite } from "@/lib/edu-guest-invite.server";

type LooseDb = {
  from: (table: string) => any; // eslint-disable-line @typescript-eslint/no-explicit-any
};
const db = supabaseAdmin as unknown as LooseDb;
const SITE = "https://yolutap.com";

async function assertAdmin(context: { supabase: unknown; userId: string }) {
  const sb = context.supabase as LooseDb;
  const { data: role } = await sb
    .from("user_roles")
    .select("role")
    .eq("user_id", context.userId)
    .eq("role", "admin")
    .maybeSingle();
  if (!role) throw forbidden("Forbidden: admin role required");
}

const LangSchema = z.enum(GUEST_LANGS as unknown as [GuestLang, ...GuestLang[]]);
const GradeSchema = z.enum(GUEST_GRADES as unknown as [GuestGrade, ...GuestGrade[]]);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .nullable()
    .transform((v) => (v ? v : null));

const CreateInput = z.object({
  firstName: z.string().trim().refine(isValidGuestName, "first name"),
  lastName: z
    .string()
    .trim()
    .optional()
    .nullable()
    .transform((v) => (v ? v : null))
    .refine((v) => v === null || isValidGuestName(v), "last name"),
  lang: LangSchema,
  grade: GradeSchema.optional().nullable().transform((v) => v ?? null),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .optional()
    .nullable()
    .transform((v) => (v ? v : null))
    .refine((v) => v === null || z.string().email().max(254).safeParse(v).success, "email"),
  note: optionalText(500),
});

export type CreatedInvite = { token: string; url: string; whatsapp: string; expiresAt: string };

export const createEduGuestInvite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => CreateInput.parse(input))
  .handler(async ({ data, context }): Promise<CreatedInvite> => {
    await assertAdmin(context as { supabase: unknown; userId: string });
    const token = newInviteToken();
    const expiresAt = new Date(Date.now() + INVITE_TTL_DAYS * 24 * 60 * 60_000).toISOString();
    const { error } = await db.from("edu_guest_invites").insert({
      token,
      first_name: data.firstName,
      last_name: data.lastName,
      lang: data.lang,
      grade: data.grade,
      email: data.email,
      note: data.note,
      created_by: (context as { userId: string }).userId,
      expires_at: expiresAt,
    });
    if (error) throw new Error(error.message);
    const url = SITE + guestInvitePath(data.lang, token);
    return { token, url, whatsapp: inviteWhatsAppHref(data.lang, data.firstName, url), expiresAt };
  });

export type AdminInvite = {
  token: string;
  url: string;
  whatsapp: string;
  firstName: string;
  lastName: string | null;
  lang: GuestLang;
  grade: string | null;
  email: string | null;
  note: string | null;
  createdAt: string;
  expiresAt: string;
  expired: boolean;
  openedAt: string | null;
  /** Статус подбора по приглашению: не начат / начат / завершён. */
  run: "none" | "started" | "completed";
};

export const listEduGuestInvites = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ rows: AdminInvite[]; tableMissing: boolean }> => {
    await assertAdmin(context as { supabase: unknown; userId: string });
    const { data, error } = await db
      .from("edu_guest_invites")
      .select("token, first_name, last_name, lang, grade, email, note, created_at, expires_at, opened_at, guest_run_id, edu_guest_runs!edu_guest_invites_guest_run_id_fkey(status)")
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) {
      if (/does not exist|schema cache/i.test(error.message)) return { rows: [], tableMissing: true };
      throw new Error(error.message);
    }
    const now = Date.now();
    return {
      tableMissing: false,
      rows: ((data ?? []) as Array<Record<string, unknown>>).map((r) => {
        const lang = r.lang as GuestLang;
        const url = SITE + guestInvitePath(lang, String(r.token));
        const runRow = r.edu_guest_runs as { status?: string } | null;
        return {
          token: String(r.token),
          url,
          whatsapp: inviteWhatsAppHref(lang, String(r.first_name), url),
          firstName: String(r.first_name),
          lastName: (r.last_name as string | null) ?? null,
          lang,
          grade: (r.grade as string | null) ?? null,
          email: (r.email as string | null) ?? null,
          note: (r.note as string | null) ?? null,
          createdAt: String(r.created_at),
          expiresAt: String(r.expires_at),
          expired: Date.parse(String(r.expires_at)) < now,
          openedAt: (r.opened_at as string | null) ?? null,
          run: !r.guest_run_id ? "none" : runRow?.status === "completed" ? "completed" : "started",
        };
      }),
    };
  });

const TokenInput = z.object({ token: z.string().regex(INVITE_TOKEN_RE), markOpened: z.boolean().optional() });

/**
 * Приглашение для страницы и старта. markOpened — страница приглашения
 * отмечает первое открытие; старт не отмечает.
 */
export const getEduGuestInvite = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => TokenInput.parse(input))
  .handler(async ({ data }): Promise<InviteView> => {
    const inv = await readInvite(data.token);
    if (!inv) return { found: false };
    if (inv.expired) return { found: true, expired: true, lang: inv.lang };
    if (data.markOpened && !inv.openedAt) {
      await db
        .from("edu_guest_invites")
        .update({ opened_at: new Date().toISOString() })
        .eq("id", inv.id)
        .is("opened_at", null);
    }
    return {
      found: true,
      expired: false,
      lang: inv.lang,
      firstName: inv.firstName,
      lastName: inv.lastName,
      grade: inv.grade,
      email: inv.email,
      origin: previewOrigin(),
    };
  });

/** Для <head> страницы приглашения: только имя и язык (og:title). */
export const getEduGuestInviteHead = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => z.object({ token: z.string() }).parse(input))
  .handler(async ({ data }): Promise<{ firstName: string | null; lang: GuestLang | null }> => {
    try {
      const inv = await readInvite(data.token);
      if (!inv || inv.expired) return { firstName: null, lang: inv?.lang ?? null };
      return { firstName: inv.firstName, lang: inv.lang };
    } catch {
      return { firstName: null, lang: null };
    }
  });
